import { useMemo } from "react";
import { PULP_OUTLINE_COLOR } from "./findingPalette.config";
import { getSinglePenStyle } from "./singlePenStyles";

// =====================================================================
// ChartFindingsLegend — small chip strip below the dental chart that
// names every finding code currently painted on the patient's chart,
// each rendered with its catalog color. Only codes actually in use
// appear; an empty chart hides the legend entirely (no "0 items" row).
//
// Lives inside ChartingTab right below ToothChart, so it shows in BOTH
// the Patient profile Charting tab AND the Doctor chairside chart mode
// (those views share the same component).
// =====================================================================

export default function ChartFindingsLegend({
  state,
  findings = [],
  label = "Legend",
  ariaLabel = "Findings on this chart",
}) {
  const entries = useMemo(() => {
    const codesPresent = new Set();
    for (const tooth of Object.values(state?.teeth ?? {})) {
      for (const f of tooth?.findings ?? []) {
        if (f?.finding_code) codesPresent.add(f.finding_code);
      }
    }
    if (codesPresent.size === 0) return [];

    // Resolve each used code against the catalog so we get color +
    // human name. Codes the catalog doesn't know about are skipped
    // silently (defensive — should never happen in normal flow).
    return findings
      .filter((f) => codesPresent.has(f.code))
      .map((f) => ({
        code: f.code,
        name: f.name,
        color: f.color_hex,
        isPulpInvolvement: !!f.is_pulp_involvement,
        singlePenStyle: getSinglePenStyle(f.code),
      }));
  }, [state, findings]);

  if (entries.length === 0) return null;

  return (
    <div
      className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 py-2 rounded-lg bg-slate-50 border border-slate-200"
      data-testid="chart-findings-legend"
      data-tutorial-id="charting-legend"
      aria-label={ariaLabel}
    >
      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </span>
      {entries.map((e) => (
        <span
          key={e.code}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700"
          data-testid={`chart-findings-legend-${e.code}`}
        >
          <LegendMark entry={e} />
          {e.name || e.code}
        </span>
      ))}
    </div>
  );
}

function LegendMark({ entry }) {
  const style = entry.singlePenStyle;
  if (!style) {
    return (
      <span
        aria-hidden
        className="inline-block w-3 h-3 rounded-full"
        style={{
          backgroundColor: entry.color || "#94a3b8",
          border: entry.isPulpInvolvement ? `1.5px solid ${PULP_OUTLINE_COLOR}` : "none",
        }}
      />
    );
  }
  if (style.renderStyle === "badge") {
    return (
      <span
        aria-hidden
        className="inline-flex h-4 min-w-6 items-center justify-center rounded border px-1 text-[8px] font-bold leading-none"
        style={{ backgroundColor: style.fillColor, borderColor: style.borderColor, color: "#7C2D12" }}
      >
        RCT
      </span>
    );
  }
  if (style.overlaySymbol === "lightningRing") {
    return (
      <svg viewBox="0 0 18 18" width="14" height="14" aria-hidden>
        <circle cx="9" cy="9" r="7.6" fill="none" stroke="#D97706" strokeWidth="1.1" strokeOpacity="0.34" />
        <path
          d="M12.2 9.4 10.9 12.5h2l-2 3.7"
          fill="none"
          stroke="#D97706"
          strokeWidth="1"
          strokeOpacity="0.48"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (style.renderStyle === "pillBadge") {
    return (
      <span
        aria-hidden
        className="inline-flex h-4 items-center rounded-full border px-1.5 text-[9px] font-semibold leading-none"
        style={{ backgroundColor: style.fillColor, borderColor: style.borderColor, color: style.textColor }}
      >
        Watch
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="inline-block w-3 h-3 rounded-sm"
      style={{
        backgroundColor: style.fillColor,
        border: `1px ${style.toothOutline === "dashed" ? "dashed" : "solid"} ${style.borderColor}`,
        opacity: entry.code === "to_extract" ? 0.75 : 1,
      }}
    />
  );
}
